import type { RawParams } from "@uirouter/core";

import Sentry from "@similarweb/sentry-logger";

import type { PageId } from "app/@types/userdata";
import type { IRouterState, RouterTrackingId } from "single-spa/react-app/router/IRouterState";
import { resolveRouterTrackingId } from "single-spa/react-app/router/resolveRouterTrackingId";

export type PageViewDropReason =
    | "missing_or_null_config"
    | "handler_did_not_track"
    | "handler_timeout"
    | "tracking_params_null";

interface IReportDroppedPageViewParams {
    reason: PageViewDropReason;
    stateName?: string;
    trackingId?: RouterTrackingId | PageId;
    pageId?: PageId;
    href?: string;
    pageViewTrackingType?: string;
}

const FINGERPRINT_PREFIX = "mixpanel-pageview-dropped";

export const shouldReportPageViewForState = (toState: Pick<IRouterState, "pageId" | "trackingId">): boolean => {
    return Boolean(toState.pageId || toState.trackingId);
};

export const getPageViewDropContextFromState = (
    toState: Pick<IRouterState, "name" | "pageId" | "trackingId">,
    toParams?: RawParams,
): Pick<IReportDroppedPageViewParams, "stateName" | "trackingId" | "pageId" | "href"> => {
    const resolvedTrackingId = resolveRouterTrackingId(toState, toParams);

    return {
        stateName: toState.name,
        trackingId: resolvedTrackingId,
        pageId: toState.pageId,
        href: window.location.href,
    };
};

export const reportDroppedPageView = (params: IReportDroppedPageViewParams): void => {
    const { reason, ...rest } = params;

    Sentry.errorTracker({
        message: `Mixpanel PageView never reached Mixpanel: ${reason}`,
        e: new Error(`${FINGERPRINT_PREFIX}:${reason}`),
        level: "warning",
        includeCustomFingerPrint: [FINGERPRINT_PREFIX, reason],
        params: {
            // Indexed as a tag so the reason distribution is searchable and aggregatable; `extra` is not.
            tags: { pageViewDropReason: reason },
            extra: {
                ...rest,
                reason,
            },
        },
    });
};
